/**
 * Bridges the Vue payment view to an owned checkout.
 *
 * Only the checkout id and the time it was remembered live in browser storage — no PII,
 * no credential, and the server still decides who may read anything. Every access is
 * wrapped so a blocked or unavailable storage never breaks the page.
 */

const KEY = 'dcx_checkout_v2'
const DEFAULT_TTL_MS = 2 * 60 * 60 * 1000

function storage() {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

export function rememberCheckout(checkoutId, { now = Date.now, store = storage() } = {}) {
  if (typeof checkoutId !== 'string' || !checkoutId) return null
  try {
    store?.setItem(KEY, JSON.stringify({ id: checkoutId, at: now() }))
  } catch {
    // Storage can be blocked; the flow still works within this page load.
  }
  return checkoutId
}

export function currentCheckout({ now = Date.now, ttlMs = DEFAULT_TTL_MS, store = storage() } = {}) {
  try {
    const raw = store?.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed.id !== 'string' || typeof parsed.at !== 'number') return null
    if (now() - parsed.at > ttlMs) return null
    return parsed.id
  } catch {
    return null
  }
}

export function forgetCheckout({ store = storage() } = {}) {
  try {
    store?.removeItem(KEY)
  } catch {
    // Nothing to do: the entry is unreachable anyway.
  }
}

/**
 * Reads the owned checkout's payment state from the server. Returns null when this browser
 * has no owned checkout, so a legacy link is never treated as one.
 */
export async function ownedPaymentState(api, { now = Date.now } = {}) {
  const checkoutId = currentCheckout({ now })
  if (!checkoutId) return null
  const view = await api.readCheckout(checkoutId)
  return { checkoutId, view }
}
